import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, serverTimestamp, runTransaction } from "firebase/firestore";
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Search, Info } from 'lucide-react';
import Spinner from './Spinner.js';
import PartsConfigTable from './components/PartsConfigTable.js';
import { useConfig } from './hooks/useConfig.js';
import useClickOutside from './hooks/useClickOutside.js';

const CreatePickupChallan = ({ db, user }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const { id } = useParams(); // PC ID for editing
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchingDC, setFetchingDC] = useState(false);
  const [deliveredDCs, setDeliveredDCs] = useState([]);
  const [firms, setFirms] = useState([]);

  // Search state for DC Picker
  const [dcSearchTerm, setDcSearchTerm] = useState('');
  const [dcSuggestions, setDcSuggestions] = useState([]);
  const [showDcSuggestions, setShowDcSuggestions] = useState(false);
  const dcSearchRef = useClickOutside(() => setShowDcSuggestions(false));

  // Search state for manual component entry
  const [bomDatabase, setBomDatabase] = useState([]);
  const [compSearchTerm, setCompSearchTerm] = useState('');
  const [compSuggestions, setCompSuggestions] = useState([]);
  const [showCompSuggestions, setShowCompSuggestions] = useState(false);
  const compSearchRef = useClickOutside(() => setShowCompSuggestions(false));
  
  const [formData, setFormData] = useState({
    pcType: 'Rental',
    referenceNo: '', // DC No
    firmId: '',
    pcNo: '',
    pickupAgent: '',
    coordinator: '',
    consigneeDetails: '',
    pickupLocation: '',
    gstin: '',
    salesperson: '',
    orderBy: { name: '', email: '', contact: '' },
    sitePerson: { name: '', email: '', contact: '' },
    remarks: '',
    components: [],
    handedOverBy: { name: '', contact: '', dateTime: '' },
    terms: `1. Pickup requests must be confirmed through our designated channels. Verbal requests or informal communications may not be considered confirmed until processed through our official booking system.											
2. Requests for cancellation or rescheduling of pickup must be made within the specified timeframe, as outlined in our cancellation policy. Failure to notify us within this timeframe may result in cancellation fees.											
3. While every effort is made to handle items with care, we are not liable for any damage, loss, or delay incurred during the pickup process, unless caused by our negligence or misconduct.											
4. Any disputes or discrepancies regarding pickuped up material i.e Missing items and damaged items shall be resolved through mutual discussion. In case of unresolved disputes, the matter may be escalated to arbitration or legal proceedings as per applicable laws and regulations.`,
    receivedBy: '',
    verifiedBy: '',
    receivedAt: new Date().toISOString().slice(0, 16),
    status: 'Draft',
    dcDate: '',
    intimationDate: new Date().toISOString().split('T')[0]
  });

  // Fetch initial data & existing PC if ID exists
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch Firms
        const firmsSnap = await getDocs(collection(db, "firms"));
        const firmsData = firmsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFirms(firmsData);

        // Fetch Delivered DCs
        const dcQuery = query(collection(db, "dispatches"), where("dispatchStatus", "==", "delivered"));
        const dcSnap = await getDocs(dcQuery);
        setDeliveredDCs(dcSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        if (id) {
          const pcSnap = await getDoc(doc(db, "pickups", id));
          if (pcSnap.exists()) {
            const pcData = pcSnap.data();
            setFormData(pcData);
            setDcSearchTerm(pcData.referenceNo || '');
          }
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id]);

  // Generate PC Number (only if not editing)
  useEffect(() => {
    if (id) return; // Don't regenerate if editing
    const generatePCNo = async () => {
      if (!formData.firmId || !formData.pcType) return;

      const selectedFirm = firms.find(f => f.id === formData.firmId);
      if (!selectedFirm) return;

      const firmShort = selectedFirm.name.includes('Nexrise') ? 'NIIPL' : 
                        selectedFirm.name.includes('Jagdish') ? 'JE' : 
                        selectedFirm.name.includes('Cloudnine') ? 'C9' : 'GEN';
      
      const typeShort = formData.pcType === 'Rental' ? 'RNT' : 'SRT';

      // Financial Year Calculation
      const now = new Date();
      const currentYear = now.getFullYear();
      const FY = now.getMonth() < 3 ? `${(currentYear-1).toString().slice(-2)}-${currentYear.toString().slice(-2)}` : `${currentYear.toString().slice(-2)}-${(currentYear+1).toString().slice(-2)}`;

      try {
        const pcNo = `PC/${firmShort}/${FY}/${typeShort}/1700`; 
        setFormData(prev => ({ ...prev, pcNo }));
      } catch (error) {
        console.error("Error generating PC No:", error);
      }
    };

    generatePCNo();
  }, [formData.firmId, formData.pcType, firms, id]);

  const handleDcSearch = (val) => {
    setDcSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = deliveredDCs.filter(dc => 
        (dc.challanNo || dc.mocNo || '').toLowerCase().includes(term) ||
        (dc.customerName || '').toLowerCase().includes(term)
      ).slice(0, 10);
      setDcSuggestions(matches);
      setShowDcSuggestions(true);
    } else {
      setShowDcSuggestions(false);
    }
  };

  // Handle DC Selection & Prefill
  const handleDCChange = async (dcDoc) => {
    setFetchingDC(true);
    setShowDcSuggestions(false);
    setDcSearchTerm(dcDoc.challanNo || dcDoc.mocNo);
    
    try {
      // Fetch original order for deeper details
      const orderSnap = await getDoc(doc(db, "orders", dcDoc.orderId));
      const orderData = orderSnap.exists() ? orderSnap.data() : {};

      // Fetch customer details for GSTIN/Email if missing
      let customerData = {};
      if (orderData.customerID) {
        const custSnap = await getDoc(doc(db, "customers", orderData.customerID));
        if (custSnap.exists()) customerData = custSnap.data();
      }

      const billingAddr = dcDoc.billingAddress || orderData.billingAddress;
      const shippingAddr = dcDoc.shippingAddress || orderData.shippingAddress;

      const consigneeText = `${dcDoc.customerName || orderData.customerName || ''}\n${typeof billingAddr === 'string' ? billingAddr : (billingAddr?.address || '') + ', ' + (billingAddr?.city || '') + ', ' + (billingAddr?.state || '')}`;
      
      const pickupText = `Contact: ${dcDoc.driverName || ''}\nProject: ${orderData.projectName || ''}\nLocation: ${typeof shippingAddr === 'string' ? shippingAddr : (shippingAddr?.address || '') + ', ' + (shippingAddr?.city || '') + ', ' + (shippingAddr?.state || '')}`;

      const fetchedGstin = dcDoc.billToGstin || orderData.billToGstin || orderData.gstin || customerData.gstin || customerData.gst_number || '';
      const fetchedBillEmail = dcDoc.billToEmail || orderData.billToEmail || billingAddr?.email || customerData.email || '';
      const fetchedShipEmail = dcDoc.shipToEmail || orderData.shipToEmail || shippingAddr?.email || '';

      // Format DC Date from dcCreatedAt (which is a Firestore Timestamp)
      let dcDateFormatted = 'N/A';
      if (dcDoc.dcCreatedAt) {
        const d = dcDoc.dcCreatedAt.toDate ? dcDoc.dcCreatedAt.toDate() : new Date(dcDoc.dcCreatedAt);
        dcDateFormatted = d.toLocaleDateString('en-IN');
      } else if (dcDoc.dispatchDate) {
        dcDateFormatted = new Date(dcDoc.dispatchDate).toLocaleDateString('en-IN');
      }

      setFormData(prev => ({
        ...prev,
        referenceNo: dcDoc.challanNo || dcDoc.mocNo || '',
        dcDate: dcDateFormatted,
        firmId: dcDoc.firmId || orderData.firmId || '',
        consigneeDetails: consigneeText,
        pickupLocation: pickupText,
        gstin: fetchedGstin,
        salesperson: orderData.salesPerson || '',
        orderBy: {
          name: orderData.primary_contact?.name || billingAddr?.attention || '',
          email: fetchedBillEmail,
          contact: orderData.primary_contact?.phone || billingAddr?.phone || ''
        },
        sitePerson: {
          name: dcDoc.driverName || shippingAddr?.attention || '',
          email: fetchedShipEmail,
          contact: dcDoc.driverContact || shippingAddr?.phone || ''
        },
        components: (dcDoc.components || []).map(comp => {
          const originalTotal = Number(comp.newQty || 0) + Number(comp.rentalQty || 0);
          return {
            ...comp,
            quantity: originalTotal,
            liftedGood: 0,
            liftedDamage: 0,
            totalLifted: 0,
            totalActive: originalTotal,
            finalMissing: 0
          };
        })
      }));
    } catch (error) {
      console.error("Error prefilling from DC:", error);
    } finally {
      setFetchingDC(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleComponentChange = (index, field, value) => {
    const updatedComponents = [...formData.components];
    const comp = updatedComponents[index];
    
    let val = Number(value);
    
    if (field === 'quantity') {
      comp.quantity = val;
    } else {
      const originalQty = Number(comp.quantity || 0);
      const currentGood = field === 'liftedGood' ? val : Number(comp.liftedGood || 0);
      const currentDamage = field === 'liftedDamage' ? val : Number(comp.liftedDamage || 0);
      const currentMissing = field === 'finalMissing' ? val : Number(comp.finalMissing || 0);

      if (currentGood + currentDamage + currentMissing > originalQty) {
        alert(`Sum of Good, Damage, and Missing (${currentGood + currentDamage + currentMissing}) cannot exceed total quantity (${originalQty})`);
        if (field === 'liftedGood') val = originalQty - (currentDamage + currentMissing);
        if (field === 'liftedDamage') val = originalQty - (currentGood + currentMissing);
        if (field === 'finalMissing') val = originalQty - (currentGood + currentDamage);
      }
      comp[field] = val;
    }
    
    // Recalculate totals for the row
    const currentQty = Number(comp.quantity || 0);
    comp.totalLifted = Number(comp.liftedGood || 0) + Number(comp.liftedDamage || 0);
    comp.totalActive = currentQty - (comp.totalLifted + Number(comp.finalMissing || 0));

    setFormData(prev => ({ ...prev, components: updatedComponents }));
  };

  const deleteComponent = (index) => {
    const updated = formData.components.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const handleCompSearch = (val) => {
    setCompSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = bomDatabase.filter(p => 
        (p.name || '').toLowerCase().includes(term) ||
        (p.sku || '').toLowerCase().includes(term)
      ).slice(0, 10);
      setCompSuggestions(matches);
      setShowCompSuggestions(true);
    } else {
      setShowCompSuggestions(false);
    }
  };

  const addComponent = (product) => {
    let toAdd = [];
    if (product.type === 'preset') {
      toAdd = product.presetComponents.map(pc => {
        const comp = bomDatabase.find(b => b.sku === pc.sku);
        return { ...comp, quantity: pc.qty, liftedGood: 0, liftedDamage: 0, totalLifted: 0, totalActive: pc.qty, finalMissing: 0 };
      });
    } else {
      toAdd.push({ ...product, quantity: 1, liftedGood: 0, liftedDamage: 0, totalLifted: 0, totalActive: 1, finalMissing: 0 });
    }

    const updated = [...formData.components];
    toAdd.forEach(newComp => {
      const idx = updated.findIndex(c => c.sku === newComp.sku);
      if (idx > -1) {
        updated[idx].quantity += newComp.quantity;
        updated[idx].totalActive += newComp.quantity;
      } else {
        updated.push(newComp);
      }
    });

    setFormData(prev => ({ ...prev, components: updated }));
    setCompSearchTerm('');
    setShowCompSuggestions(false);
  };

  const handleSave = async (status) => {
    if (status === 'Confirmed' && !window.confirm("Are you sure you want to confirm this Pickup Challan? This will move it to the Inwards Que.")) return;
    
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        status: status,
        updatedAt: serverTimestamp(),
        updatedBy: user.displayName || user.email
      };

      if (id) {
        // Update existing
        await updateDoc(doc(db, "pickups", id), payload);
      } else {
        // Create new
        await addDoc(collection(db, "pickups"), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: user.displayName || user.email
        });
      }
      
      alert(`Pickup Challan ${status === 'Draft' ? 'Saved as Draft' : 'Confirmed'} Successfully.`);
      navigate('/ops/pickup-challan');
    } catch (error) {
      console.error("Error saving PC:", error);
      alert("Error saving PC. Check console.");
    } finally {
      setIsSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db',
    fontSize: '14px', outline: 'none', marginBottom: '15px'
  };

  const labelStyle = {
    display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#475569'
  };

  const sectionHeaderStyle = {
    fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', 
    paddingBottom: '10px', borderBottom: '2px solid #e2e8f0', marginTop: '30px'
  };

  if (loading) return <div style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spinner /></div>;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <button onClick={() => navigate('/ops/pickup-challan')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', color: '#64748b' }}>
          <ArrowLeft size={20} /> Back to List
        </button>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>{id ? 'Edit' : 'Initiate'} Pickup Challan</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => handleSave('Draft')} disabled={isSaving} style={{ backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Save size={18} /> {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          <button onClick={() => handleSave('Confirmed')} disabled={isSaving} style={{ backgroundColor: '#059669', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <Plus size={18} /> {isSaving ? 'Confirming...' : 'Confirm PC'}
          </button>
        </div>
      </div>

      <form style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} onSubmit={(e) => e.preventDefault()}>
        
        {/* Step 1: PC Type & Reference */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Pickup Challan Type</label>
            <select style={inputStyle} value={formData.pcType} onChange={(e) => handleInputChange('pcType', e.target.value)}>
              <option value="Rental">Rental</option>
              <option value="Sale Return">Sale Return</option>
            </select>
          </div>

          <div style={{ position: 'relative' }} ref={dcSearchRef}>
            <label style={labelStyle}>Delivered DC Picker (Search by No or Customer)</label>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' }}>
              <Search size={18} color="#64748b" />
              <input 
                type="text" 
                placeholder="Find delivered DC..." 
                value={dcSearchTerm} 
                onChange={(e) => handleDcSearch(e.target.value)} 
                onFocus={() => dcSearchTerm.length > 1 && setShowDcSuggestions(true)}
                style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
              />
              {fetchingDC && <Spinner size="small" />}
            </div>
            {showDcSuggestions && (
              <div style={{ position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                {dcSuggestions.map(dc => (
                  <div key={dc.id} onClick={() => handleDCChange(dc)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                    <div style={{ fontWeight: 'bold' }}>{dc.challanNo || dc.mocNo}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{dc.customerName}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Basic Details */}
        <div style={sectionHeaderStyle}>Basic Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Firm Picker</label>
            <select style={inputStyle} value={formData.firmId} onChange={(e) => handleInputChange('firmId', e.target.value)}>
              <option value="">-- Select Firm --</option>
              {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>PC No. (Auto-generated)</label>
            <input type="text" style={{ ...inputStyle, backgroundColor: '#f8fafc' }} value={formData.pcNo} readOnly />
          </div>
          <div>
            <label style={labelStyle}>Intimation Date</label>
            <input type="date" style={inputStyle} value={formData.intimationDate} onChange={(e) => handleInputChange('intimationDate', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Pickup Agent</label>
            <select style={inputStyle} value={formData.pickupAgent} onChange={(e) => handleInputChange('pickupAgent', e.target.value)}>
              <option value="">-- Select Agent --</option>
              <option value="Shahid">Shahid</option>
              <option value="Prashant">Prashant</option>
              <option value="Shakti">Shakti</option>
              <option value="Self">Self</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Coordinator</label>
            <select style={inputStyle} value={formData.coordinator} onChange={(e) => handleInputChange('coordinator', e.target.value)}>
              <option value="">-- Select Coordinator --</option>
              <option value="Rajesh">Rajesh</option>
              <option value="Ghanshyam">Ghanshyam</option>
              <option value="Ankit">Ankit</option>
            </select>
          </div>
        </div>

        {/* Party Details */}
        <div style={sectionHeaderStyle}>Party & Contact Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          <div>
            <label style={labelStyle}>Consignee Details (Billing)</label>
            <textarea style={{ ...inputStyle, height: '100px' }} value={formData.consigneeDetails} onChange={(e) => handleInputChange('consigneeDetails', e.target.value)} placeholder="Customer Name & Billing Address" />
          </div>
          <div>
            <label style={labelStyle}>Pickup Location (Site)</label>
            <textarea style={{ ...inputStyle, height: '100px' }} value={formData.pickupLocation} onChange={(e) => handleInputChange('pickupLocation', e.target.value)} placeholder="Shipping Person, Project Name & Address" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <label style={labelStyle}>GSTIN/UIN</label>
            <input type="text" style={inputStyle} value={formData.gstin} onChange={(e) => handleInputChange('gstin', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Salesperson</label>
            <select style={inputStyle} value={formData.salesperson} onChange={(e) => handleInputChange('salesperson', e.target.value)}>
              <option value="">-- Select Salesperson --</option>
              {config.sales_team?.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '10px' }}>
          {/* Billing Contact */}
          <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
            <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '10px' }}>Order By (Billing Contact)</div>
            <label style={labelStyle}>Name</label>
            <input type="text" style={inputStyle} value={formData.orderBy.name} onChange={(e) => handleNestedInputChange('orderBy', 'name', e.target.value)} />
            <label style={labelStyle}>Email</label>
            <input type="email" style={inputStyle} value={formData.orderBy.email} onChange={(e) => handleNestedInputChange('orderBy', 'email', e.target.value)} />
            <label style={labelStyle}>Contact</label>
            <input type="text" style={inputStyle} value={formData.orderBy.contact} onChange={(e) => handleNestedInputChange('orderBy', 'contact', e.target.value)} />
          </div>
          {/* Site Contact */}
          <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
            <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '10px' }}>Site Person (Shipping Contact)</div>
            <label style={labelStyle}>Name</label>
            <input type="text" style={inputStyle} value={formData.sitePerson.name} onChange={(e) => handleNestedInputChange('sitePerson', 'name', e.target.value)} />
            <label style={labelStyle}>Email</label>
            <input type="email" style={inputStyle} value={formData.sitePerson.email} onChange={(e) => handleNestedInputChange('sitePerson', 'email', e.target.value)} />
            <label style={labelStyle}>Contact</label>
            <input type="text" style={inputStyle} value={formData.sitePerson.contact} onChange={(e) => handleNestedInputChange('sitePerson', 'contact', e.target.value)} />
          </div>
        </div>

        <div>
          <label style={{ ...labelStyle, marginTop: '20px' }}>Remarks</label>
          <textarea style={{ ...inputStyle, height: '60px' }} value={formData.remarks} onChange={(e) => handleInputChange('remarks', e.target.value)} />
        </div>

        {/* Component Table */}
        <div style={{ ...sectionHeaderStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Component Table</span>
          <div style={{ position: 'relative', width: '350px' }} ref={compSearchRef}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
              <Search size={16} color="#64748b" />
              <input 
                type="text" 
                placeholder="Search Presets/Components to add..." 
                value={compSearchTerm}
                onChange={(e) => handleCompSearch(e.target.value)}
                onFocus={() => compSearchTerm.length > 1 && setShowCompSuggestions(true)}
                style={{ border: 'none', background: 'transparent', marginLeft: '8px', outline: 'none', width: '100%', fontSize: '13px' }}
              />
            </div>
            {showCompSuggestions && (
              <div style={{ position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxHeight: '300px', overflowY: 'auto' }}>
                {compSuggestions.map(p => (
                  <div key={p.id} onClick={() => addComponent(p)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                    <strong>{p.sku}</strong> - {p.name} <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase' }}>({p.type})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <PartsConfigTable 
          mode="pickup"
          components={formData.components}
          onChange={handleComponentChange}
          onDelete={deleteComponent}
        />

        {/* Handover Details */}
        <div style={sectionHeaderStyle}>Handover Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Handed Over By (Name)</label>
            <input type="text" style={inputStyle} value={formData.handedOverBy.name} onChange={(e) => handleNestedInputChange('handedOverBy', 'name', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Contact No.</label>
            <input type="text" style={inputStyle} value={formData.handedOverBy.contact} onChange={(e) => handleNestedInputChange('handedOverBy', 'contact', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Date & Time of Handover</label>
            <input type="datetime-local" style={inputStyle} value={formData.handedOverBy.dateTime} onChange={(e) => handleNestedInputChange('handedOverBy', 'dateTime', e.target.value)} />
          </div>
        </div>

        {/* Terms and Conditions */}
        <div style={sectionHeaderStyle}>Terms and Conditions</div>
        <textarea 
          style={{ ...inputStyle, height: '150px', fontSize: '12px', lineHeight: '1.5' }} 
          value={formData.terms} 
          onChange={(e) => handleInputChange('terms', e.target.value)} 
        />

        {/* Warehouse Receipt */}
        <div style={sectionHeaderStyle}>Warehouse Receipt / Verification</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Received at warehouse by</label>
            <select style={inputStyle} value={formData.receivedBy} onChange={(e) => handleInputChange('receivedBy', e.target.value)}>
              <option value="">-- Select Person --</option>
              <option value="Shakti">Shakti</option>
              <option value="Prashant">Prashant</option>
              <option value="Sachin">Sachin</option>
              <option value="Shahid">Shahid</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Time and Date</label>
            <input type="datetime-local" style={inputStyle} value={formData.receivedAt} onChange={(e) => handleInputChange('receivedAt', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Verified and Checked By</label>
            <select style={inputStyle} value={formData.verifiedBy} onChange={(e) => handleInputChange('verifiedBy', e.target.value)}>
              <option value="">-- Select Person --</option>
              <option value="Shakti">Shakti</option>
              <option value="Prashant">Prashant</option>
              <option value="Sachin">Sachin</option>
              <option value="Shahid">Shahid</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
          <button type="button" onClick={() => navigate('/ops/pickup-challan')} style={{ padding: '12px 25px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600' }}>
            Cancel
          </button>
          <button type="button" onClick={() => handleSave('Draft')} disabled={isSaving} style={{ backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', padding: '12px 30px', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          <button type="button" onClick={() => handleSave('Confirmed')} disabled={isSaving} style={{ backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '12px 40px', borderRadius: '8px', fontWeight: '700', fontSize: '16px', cursor: 'pointer' }}>
            {isSaving ? 'Confirming...' : 'Confirm PC'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default CreatePickupChallan;